import { RadioGroup } from "@headlessui/react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import { RadioIndicator } from "@/components/Interface/FormFields/RadioButton/RadioButton";
import { getTariffType } from "@/utils/deviceUtils";
import {
  getDataSpeedSummaryForRateSelection,
  getRoamingSummaryForRateSelection,
  stripCurrencyPrefix,
  toSwissPriceNotation,
  TransformedProduct,
} from "@/utils/tariffUtils";

export interface RateCardProps {
  dataTestid?: string;
  product: TransformedProduct;
  disabled?: boolean;
  originalPrice?: string;
  /** When set, shows a "current tariff" chip with this label next to the product name. */
  currentLabel?: string;
  /**
   * `radio` (default): selectable option inside a RadioGroup.
   * `display`: same tile look without selection chrome (e.g. FWB welcome).
   */
  selectionMode?: "radio" | "display";
}

/**
 * One tariff tile: price, name and what the tariff includes abroad.
 * In `radio` mode the radio sits in the card's top right corner.
 */
export const RateCard = ({
  dataTestid,
  product,
  disabled = false,
  originalPrice,
  currentLabel,
  selectionMode = "radio",
}: RateCardProps) => {
  const { t } = useTranslation();

  if (!product.id) {
    return null;
  }

  const tariffType = getTariffType(product.id);
  const roamingSummary = getRoamingSummaryForRateSelection(
    t,
    tariffType,
    product.minContractDur || 30,
  );
  const dataSpeedSummary = getDataSpeedSummaryForRateSelection(
    t,
    tariffType,
    product.bandwidthLabel,
  );

  const testId = dataTestid ?? `option-${product.id}`;

  const renderBody = (checked: boolean) => (
    <div
      className={clsx(
        "flex h-full flex-col gap-2 rounded-lg bg-surface-accent-ivory p-4",
        selectionMode === "radio" && "cursor-pointer",
        selectionMode === "radio" &&
          checked &&
          "outline outline-2 outline-foreground-primary",
        disabled && "cursor-not-allowed opacity-50",
        currentLabel && "border border-border-tertiary",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-start gap-x-2 gap-y-0">
            <p className="text-5xl font-semibold leading-xs tracking-[-0.02em] text-foreground-primary">
              {toSwissPriceNotation(stripCurrencyPrefix(product.chargeFlat))}
            </p>
            {originalPrice && (
              <p className="text-xl font-semibold leading-md text-foreground-primary line-through">
                {toSwissPriceNotation(originalPrice)}
              </p>
            )}
          </div>
          <p className="text-xs leading-md text-foreground-secondary">
            {t("Onboarding.sections.rate-selection.perMonth")}
          </p>
        </div>
        {selectionMode === "radio" && (
          <RadioIndicator checked={checked} disabled={disabled} />
        )}
      </div>

      <div className="flex items-center gap-2">
        <p className="text-xl font-semibold leading-md text-foreground-primary">
          {product.baseDisplayValue}
        </p>
        {currentLabel && (
          <span
            data-testid={`current-rate-chip-${product.id}`}
            className="inline-flex h-6 shrink-0 items-center rounded-full bg-surface-quaternary px-2 py-1 text-body-xs font-semibold leading-none tracking-[0.24px] text-foreground-info"
          >
            {currentLabel}
          </span>
        )}
      </div>

      {roamingSummary && (
        <div className="flex flex-col gap-1">
          <p className="text-xs font-semibold leading-none text-foreground-primary">
            {t("portal:mobileAbo.features.international")}
          </p>
          <p className="text-body-sm font-semibold leading-md text-foreground-primary">
            {roamingSummary.headline}
          </p>
          <p className="text-xs leading-none text-foreground-secondary">
            {roamingSummary.caption}
          </p>
        </div>
      )}

      {dataSpeedSummary && (
        <div className="flex items-start gap-2">
          <div className="flex flex-1 flex-col gap-1">
            <p className="text-body-sm font-semibold leading-md text-foreground-primary">
              {dataSpeedSummary.download}
            </p>
            <p className="text-xs leading-none text-foreground-secondary">
              {dataSpeedSummary.downloadLabel}
            </p>
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <p className="text-body-sm font-semibold leading-md text-foreground-primary">
              {dataSpeedSummary.upload}
            </p>
            <p className="text-xs leading-none text-foreground-secondary">
              {dataSpeedSummary.uploadLabel}
            </p>
          </div>
        </div>
      )}
    </div>
  );

  if (selectionMode === "display") {
    return <div data-testid={testId}>{renderBody(false)}</div>;
  }

  return (
    <RadioGroup.Option
      data-testid={testId}
      value={product.id.toString()}
      disabled={disabled}
    >
      {({ checked }) => renderBody(checked)}
    </RadioGroup.Option>
  );
};
